import { Link, useParams } from "react-router-dom";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Reveal from "@/components/feature/Reveal";
import PostCard from "@/components/feature/PostCard";
import ZoneCard from "@/components/feature/ZoneCard";
import { zones } from "@/mocks/zones";
import { posts } from "@/mocks/posts";

export default function ZoneDetail() {
  const { id } = useParams<{ id: string }>();
  const zone = zones.find((item) => item.id === id);
  const zonePosts = posts
    .filter((post) => post.zoneId === id)
    .sort((a, b) => (a.date < b.date ? 1 : -1));
  const relatedZones = zones.filter((item) => item.id !== zone?.id && item.category === zone?.category);
  const fallbackZones = zones.filter((item) => item.id !== zone?.id).slice(0, 3);
  const suggestions = relatedZones.length > 0 ? relatedZones : fallbackZones;

  useSeo({
    title: zone
      ? `${zone.name}专区 - 爆料91${zone.name}合集全收录`
      : "专区未找到 - 爆料91",
    description: zone
      ? `${zone.name}专区整理了${zone.subtitle}相关的全部合集，${zone.description}`
      : "该专区不存在或已下线，可返回爆料91专区总览继续浏览其他合集。",
    keywords: zone
      ? `爆料91,${zone.name},${zone.name}专区,${zone.tags.join(",")}`
      : "爆料91专区,专区导航",
    path: `/zone/${id ?? ""}`,
    type: "website",
    image: zone?.cover,
    jsonLd: zone
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${zone.name}专区`,
            url: `${SITE_ORIGIN}/zone/${zone.id}`,
            inLanguage: "zh-CN",
            description: zone.description,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
              { "@type": "ListItem", position: 2, name: "专区总览", item: `${SITE_ORIGIN}/zones` },
              { "@type": "ListItem", position: 3, name: zone.name, item: `${SITE_ORIGIN}/zone/${zone.id}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${zone.name}合集内容`,
            itemListElement: zonePosts.map((post, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: post.title,
              url: `${SITE_ORIGIN}/post/${post.id}`,
            })),
          },
        ]
      : undefined,
  });

  if (!zone) {
    return (
      <section className="w-full px-4 md:px-8 py-20 md:py-28">
        <div className="max-w-xl mx-auto text-center rounded-lg border border-background-200 bg-background-100 py-16 px-6">
          <i className="ri-compass-3-line text-4xl text-foreground-400"></i>
          <h1 className="mt-5 font-heading font-bold text-2xl text-foreground-950">没有找到这个专区</h1>
          <p className="mt-3 text-sm text-foreground-600">
            专区编号可能有误，或该栏目已调整归属。回到专区总览可以继续浏览全部合集。
          </p>
          <Link
            to="/zones"
            className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
          >
            <i className="ri-layout-grid-fill"></i>
            返回专区总览
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative w-full overflow-hidden border-b border-background-200">
        <div className="absolute inset-0">
          <img
            src={zone.cover}
            alt={`${zone.name}专区封面`}
            title={`${zone.name} - 爆料91专区合集`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/75 to-foreground-950/40"></div>
        </div>

        <div className="relative w-full px-4 md:px-8 pt-6 pb-12 md:pb-16">
          <div className="[&_a]:text-background-200 [&_span]:text-background-100 [&_i]:text-background-300">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "专区总览", to: "/zones" },
                { label: zone.name },
              ]}
            />
          </div>

          <div className="mt-8 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-primary-600 text-background-50">
              {zone.category}
            </span>
            <h1 className="mt-4 font-heading font-black text-3xl md:text-5xl text-background-50">
              {zone.name}
            </h1>
            <p className="mt-3 font-heading text-lg text-accent-300">{zone.subtitle}</p>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-background-200">
              {zone.description}
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {zone.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1.5 rounded-full bg-background-50/15 text-background-100 border border-background-50/25"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <dl className="mt-8 flex flex-wrap gap-8">
              <div>
                <dd className="font-label text-2xl text-accent-300">{zone.count}</dd>
                <dt className="mt-1 text-xs text-background-300">在架合集期数</dt>
              </div>
              <div>
                <dd className="font-label text-2xl text-accent-300">{zonePosts.length}</dd>
                <dt className="mt-1 text-xs text-background-300">本页可读条目</dt>
              </div>
              <div>
                <dd className="font-label text-2xl text-accent-300">{zone.updatedAt}</dd>
                <dt className="mt-1 text-xs text-background-300">最近整理时间</dt>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-8">
              <div className="flex items-end justify-between gap-4">
                <h2 className="font-heading font-bold text-2xl text-foreground-950">
                  本专区合集条目
                </h2>
                <span className="text-sm text-foreground-500">按整理时间排序</span>
              </div>

              {zonePosts.length > 0 ? (
                <div className="mt-6 flex flex-col gap-4">
                  {zonePosts.map((post, index) => (
                    <Reveal key={post.id} delay={index * 60}>
                      <PostCard post={post} variant="list" />
                    </Reveal>
                  ))}
                </div>
              ) : (
                <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 py-14 text-center">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                  <p className="mt-4 text-sm text-foreground-600">
                    这个专区的内容正在整理中，稍后再来看看。
                  </p>
                </div>
              )}
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading font-bold text-lg text-foreground-950">专区说明</h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  本专区属于「{zone.category}」分类，收录方向为{zone.subtitle}。
                  上方条目按整理时间倒序排列，越靠前的内容越新。
                </p>
                <div className="mt-4 pt-4 border-t border-background-200 flex flex-col gap-2 text-sm">
                  <span className="flex items-center gap-2 text-foreground-700">
                    <i className="ri-price-tag-3-line text-accent-700"></i>
                    {zone.tags.join(" · ")}
                  </span>
                  <span className="flex items-center gap-2 text-foreground-700">
                    <i className="ri-refresh-line text-accent-700"></i>
                    更新于 {zone.updatedAt}
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading font-bold text-lg text-foreground-950">相关专区</h3>
                <div className="mt-4 flex flex-col gap-3">
                  {suggestions.map((item) => (
                    <Link
                      key={item.id}
                      to={`/zone/${item.id}`}
                      className="group flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <span className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-700 transition-colors">
                          {item.name}
                        </span>
                        <span className="mt-1 text-xs text-foreground-500">{item.subtitle}</span>
                      </span>
                      <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all"></i>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-t border-background-200">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <h2 className="font-heading font-bold text-2xl text-foreground-950">继续逛逛其他专区</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {suggestions.slice(0, 3).map((item, index) => (
              <Reveal key={item.id} delay={index * 60}>
                <ZoneCard zone={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}